import { memo } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * EmptyState component - Reusable empty state with background image
 * @param {string} title - Title text to display
 * @param {string} message - Message text to display
 * @param {string} image - Optional background image URL
 * @param {React.ReactNode} actionButton - Optional primary action button (e.g. "Create Campaign", "Invite Influencer")
 * @param {object} sx - Additional sx styles
 */
export const EmptyState = memo(({ title, message, image, actionButton, sx }) => {
  return (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      role="status"
      aria-live="polite"
    >
      {image && (
        <Box
          component="img"
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.1,
            filter: 'blur(4px)',
          }}
        />
      )}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: actionButton ? 3 : 0 }}>
          {message}
        </Typography>
        {actionButton ? <Box sx={{ mt: 2 }}>{actionButton}</Box> : null}
      </Box>
    </Paper>
  )
})

EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  image: PropTypes.string,
  actionButton: PropTypes.node,
  sx: PropTypes.object,
}

EmptyState.displayName = 'EmptyState'
